import { useQuery } from '@apollo/client'
import { downloadFileWithReportKey } from '@faceup/crypto'
import { FormattedMessage, useIntl, useLocalization } from '@faceup/localization'
import { type Attachment, MoreInformationContent, useFormItemAnswers } from '@faceup/report'
import { AttachmentList } from '@faceup/ui'
import { useMessage } from '@faceup/ui-base'
import { FormItemType, getTranslation as utilsGetTranslation } from '@faceup/utils'
import { type ReactNode, Suspense } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { ReportRow } from '../../Pages/Report/components'
import { sharedMessages } from '../../Shared/translations'
import { messages } from './messages'
import { sortByCategoryOrder } from './sortByCategoryOrder'

const fragments = {
  SentInformationContent_case: graphql(`
    fragment SentInformationContent_case on CaseInterface {
      id
      organizationalUnit {
        id
        name
      }
      attachments {
        id
        name
        url
        mimetype
      }
      channel {
        id
        defaultLanguage
        categories {
          id
        }
        organizationalUnits {
          id
        }
        config {
          ... on FormReportSourceConfiguration {
            id
            formItems {
              id
              type
              labelTranslations {
                language
                translation
              }
            }
          }
        }
      }
      answers {
        id
        values
        formItem {
          id
          type
        }
      }
      ...FormItemsAnswers_case
    }
  `),
  SentInformationContent_companyReport: graphql(`
    fragment SentInformationContent_companyReport on CompanyReport {
      id
      encryptionKey
      category {
        id
        name
      }
      ...UseReportBody_reportNoVariables
    }
  `),
}

const queries = {
  SentInformationFormItemsOrder: graphql(`
    query SentInformationFormItemsOrder(
      $categoryId: CompanyReportCategoryGlobalId
    ) {
      companyVictimViewer {
        id
        case {
          id
          channel {
            id
            config {
              ... on FormReportSourceConfiguration {
                id
                formItems(categoryId: $categoryId) {
                  id
                  order
                }
              }
            }
          }
        }
      }
    }
  `),
}

type SentInformationContentProps = {
  aCase: FragmentType<typeof fragments.SentInformationContent_case>
  report: FragmentType<typeof fragments.SentInformationContent_companyReport> | null
}

export const SentInformationContent = (props: SentInformationContentProps) => {
  const aCase = getFragmentData(fragments.SentInformationContent_case, props.aCase)
  const report = props.report
    ? getFragmentData(fragments.SentInformationContent_companyReport, props.report)
    : null
  const { formatMessage } = useIntl()
  const { language } = useLocalization()
  const message = useMessage()
  const orgUnitName = aCase.organizationalUnit?.name ?? null
  const attachments = (aCase.attachments ?? []).map(({ id, name, url, mimetype }) => ({
    id,
    name,
    url,
    mimetype,
  }))

  const handleDownload = async (attachment: Attachment) => {
    const result = await downloadFileWithReportKey(attachment, report?.encryptionKey ?? '')
    if (result.isErr()) {
      message.error(formatMessage(sharedMessages.apiError))
      return false
    }
    return true
  }

  const channelConfig =
    aCase.channel.config?.__typename === 'FormReportSourceConfiguration'
      ? aCase.channel.config
      : null

  const reportCategoryId = report?.category?.id ?? null
  const { data: orderData } = useQuery(queries.SentInformationFormItemsOrder, {
    variables: { categoryId: reportCategoryId },
    skip: !reportCategoryId,
  })
  const orderedConfig = orderData?.companyVictimViewer?.case?.channel.config
  const categoryOrderByItemId = new Map(
    (orderedConfig?.__typename === 'FormReportSourceConfiguration'
      ? orderedConfig.formItems
      : []
    ).map(item => [item.id, item.order ?? 0])
  )

  const formItemAnswerMap = new Map(
    useFormItemAnswers(aCase, aCase.channel.defaultLanguage).map(a => [a.formItemId, a])
  )

  const getLabel = (
    labelTranslations: { language: string; translation: string }[],
    fallback: ReactNode
  ): ReactNode => {
    const label = utilsGetTranslation(labelTranslations, language, aCase.channel.defaultLanguage)
    return label || fallback
  }

  const sortedFormItems = sortByCategoryOrder(channelConfig?.formItems ?? [], categoryOrderByItemId)

  // Classroom value comes from the decrypted `values[0]` on the answer, not from translatedValues
  const classroomAnswer = aCase.answers.find(a => a.formItem.type === FormItemType.Classroom)

  return (
    <div className='flex flex-col gap-3'>
      {sortedFormItems.map(formItem => {
        switch (formItem.type) {
          case FormItemType.Category:
            if (aCase.channel.categories.length <= 1 || !report?.category) {
              return null
            }
            return (
              <ReportRow
                key={formItem.id}
                label={getLabel(
                  formItem?.labelTranslations,
                  <FormattedMessage message={messages.category} />
                )}
              >
                {report.category.name}
              </ReportRow>
            )

          case FormItemType.OrganizationalUnit:
            if (aCase.channel.organizationalUnits.length <= 1 || !orgUnitName) {
              return null
            }
            return (
              <ReportRow
                key={formItem.id}
                label={getLabel(
                  formItem.labelTranslations,
                  <FormattedMessage message={messages.organizationalUnit} />
                )}
              >
                {orgUnitName}
              </ReportRow>
            )

          case FormItemType.Classroom:
            if (!classroomAnswer) {
              return null
            }
            return (
              <ReportRow
                key={formItem.id}
                label={getLabel(
                  formItem?.labelTranslations,
                  <FormattedMessage message={messages.classRoom} />
                )}
              >
                {classroomAnswer.values[0] ?? ''}
              </ReportRow>
            )

          case FormItemType.MoreInformation:
            if (!report) {
              return null
            }
            return (
              <Suspense key={formItem.id} fallback={null}>
                <MoreInformationContent
                  report={{ reportNoVar: report }}
                  attachments={formItemAnswerMap.get(formItem.id)?.attachments ?? []}
                  onDownload={handleDownload}
                  renderRow={content => (
                    <ReportRow
                      label={getLabel(
                        formItem?.labelTranslations,
                        <FormattedMessage message={messages.moreInfo} />
                      )}
                    >
                      {content}
                    </ReportRow>
                  )}
                />
              </Suspense>
            )

          default: {
            const answer = formItemAnswerMap.get(formItem.id)
            if (!answer) {
              return null
            }
            if (formItem.type === FormItemType.File) {
              return (
                <ReportRow key={formItem.id} label={answer.label}>
                  <AttachmentList
                    attachments={answer.attachments ?? []}
                    config={{ onDownload: handleDownload }}
                  />
                </ReportRow>
              )
            }
            return (
              <ReportRow key={formItem.id} label={answer.label}>
                {answer.value}
              </ReportRow>
            )
          }
        }
      })}
      {attachments.length > 0 && (
        <ReportRow label={<FormattedMessage message={messages.attachments} />}>
          <AttachmentList attachments={attachments} config={{ onDownload: handleDownload }} />
        </ReportRow>
      )}
    </div>
  )
}
